import { Checkbox, Radio } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { ClickBustingConfig } from '../../config/ClickBusting';
import { DisplayFlex } from '../../styles/Flexbox';
import { TabStyles } from '../../styles/TabStyles';
import { InputAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut, PropsAndInputAttrs } from '../../utils/ReactHelpers';
import { CheckBoxProps } from './CheckBoxWidget';
import { CheckBoxWithLabelProps } from './CheckBoxWithLabelWidget';
import { RadioButtonProps } from './RadioButtonWidget';
import { RadioButtonWithLabelProps } from './RadioButtonWithLabelWidget';

export { CheckBoxProps, CheckBoxWithLabelProps, RadioButtonProps, RadioButtonWithLabelProps };

const StyleName = TypeStyle.style({
  boxSizing: 'border-box',
  width: 'fit-content',
});

const WithLabelStyleName = TypeStyle.style({
  ...DisplayFlex,
});

const LeftMarginStyleName = TypeStyle.style({
  marginLeft: TabStyles.Spacing.DefaultSpace,
});

type SelectorProps = {
  componentType: typeof Radio | typeof Checkbox;
  removeLeftMargin?: boolean;
  testId: string;
};
type LabelAttrs = React.LabelHTMLAttributes<HTMLLabelElement>;

const SelectorShim: React.FC<PropsAndInputAttrs<SelectorProps>> = props => {
  const { className, componentType: Component, removeLeftMargin, testId, ...inputProps } = props;

  return (
    <Component
      {...inputProps}
      className={TypeStyle.classes(
        StyleName,
        !!inputProps.children && WithLabelStyleName,
        !removeLeftMargin && LeftMarginStyleName,
        ClickBustingConfig.suppressionClassName,
        className,
      )}
      labelProps={{ 'data-tb-test-id': `${testId}-Label`, ...EventUtilsOptOut } as LabelAttrs}
    />
  );
};
SelectorShim.displayName = 'SelectorShim';

export class CheckBoxWithLabelWidget extends React.PureComponent<PropsAndInputAttrs<CheckBoxWithLabelProps>> {
  private handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (this.props.onChange) {
      this.props.onChange(e);
    } else {
      this.props.handleChange?.(e.target.checked);
    }
  }

  public render(): JSX.Element {
    const { containerStyle, handleChange, label, ...selectorProps } = this.props;
    return (
      <SelectorShim
        {...selectorProps}
        children={label || selectorProps.children}
        onChange={this.handleChange}
        style={containerStyle || selectorProps.style}

        componentType={Checkbox}
        data-tb-test-id={`${selectorProps.testId}-CheckBox`}
      />
    );
  }
}

export class CheckBoxWidget extends React.PureComponent<PropsAndInputAttrs<CheckBoxProps>> {
  public render(): JSX.Element {
    // eslint-disable-next-line deprecation/deprecation
    const { hover, ...checkboxProps } = this.props;
    return <CheckBoxWithLabelWidget {...checkboxProps} label='' />;
  }
}

export class RadioButtonWithLabelWidget<T = string> extends React.PureComponent<RadioButtonWithLabelProps<T>> {
  private handleChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    if (this.props.checked) { return; }
    this.props.handleChange?.(this.props.value, this.props.index);
  }

  public render(): JSX.Element {
    const { containerStyle, groupName, handleChange, index, label, value, ...selectorProps } = this.props;
    return (
      <SelectorShim
        {...selectorProps}
        children={selectorProps.children || label}
        onChange={this.handleChange}
        style={containerStyle}

        componentType={Radio}
        data-tb-test-id={`${selectorProps.testId}-RadioButton`}
        name={groupName}
        value={value as unknown as InputAttrs['value']}
      />
    );
  }
}

export class RadioButtonWidget<T = string> extends React.PureComponent<PropsAndInputAttrs<RadioButtonProps<T>>> {
  public render(): JSX.Element {
    return <RadioButtonWithLabelWidget {...this.props} label='' />;
  }
}
